import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { Drawer, Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import { Dropdown } from 'antd';
import { Button, Spinner } from '@agconnections/grow-ui';
import { isEqual } from 'lodash';
import usePopulateMapData from 'hooks/usePopulateMapData';
import { FETCH_TYPES } from 'helpers/constants';
import { getTotalAreaNumber } from 'helpers/getTotalAreaAcres';
import getSeasons from 'screens/Yield/components/YieldActions/helpers/getSeasons';
import SearchInputLandingProperties from 'screens/Property/PropertiesLanding/components/SearchInputLandingProperties';
import { Context } from 'components/Store';
import FieldIcon from 'assets/field_filled.svg';
import Divider from 'assets/divider.svg';
import InfoIcon from 'assets/info.svg';
import SortIcon from 'assets/sort.svg';
import CropIconMapping from 'components/CropIconMapping';
import useCropData from 'hooks/useCropData';
import { getSelectedCropZones } from 'screens/Reports/helpers/helperFunctions';
import { SEARCH_TEXT_VALUE_FFT } from 'reducers/reducer';
import PropertyMapWrapper from 'screens/Property/PropertiesLanding/PropertyLandingMap/PropertyMapWrapper';
import { LAST_COORDINATES_STORED } from 'screens/Property/helpers/constants';
import { calculateCentroidByEachProperties } from 'screens/Property/helpers/mapApiHelpers';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import { getStringParsed } from 'utilities/helpers';
import groupByCrop from 'screens/Property/helpers/groupByCrops';
import { usePreferences, PreferenceKey } from 'hooks/usePreferences';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import { useAuth } from 'utilities/base-auth';
import showConfirmModal from 'components/ConfirmModal';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import ReportProblemIcon from 'assets/report_problem.svg?react';
import SpinnerIcon from 'assets/spinner_modal.svg';
import matchFeaturesAndSelectedCropzones from '../../helpers/matchFeaturesAndSelectedCropzones';
import PropertySelector from '../PropertySelector';
import searchByTextWithinFFT from '../../helpers/searchByTextWithinFFT';
import './index.css';
import { filterPropertiesCropZones } from '../../helpers/filterCropZonesHelpers';

const GROUP_BY_FARM = 'farm';
const GROUP_BY_CROP = 'crop';

const SelectPropertiesDrawer = ({
  open,
  onClose,
  seasonIds,
  formikValues,
  type
}) => {
  const { setFieldValue, values } = useFormikContext();
  const [
    { cropSeasons, searchTextValueFFT, isGlobalCropSeasonLoading },
    dispatch
  ] = useContext(Context);
  const allSeasonIds = useMemo(() => getSeasons(), []);
  const selectedSeasonIds = useMemo(
    () => (seasonIds?.length ? seasonIds : allSeasonIds),
    [seasonIds, allSeasonIds]
  );
  const [properties, setProperties] = useState([]);
  const [groupBy, setGroupBy] = useState(GROUP_BY_FARM);
  const { findPreferenceByKey, upsertPreference } = usePreferences();
  const preferenceLoaded = useRef(false);
  const initiallySelectedCropZoneIdsRef = useRef(new Set());
  const [
    initiallySelectedCropZoneIdsSnapshot,
    setInitiallySelectedCropZoneIdsSnapshot
  ] = useState([]);
  const mappedCropZoneField = formikValues?.cropZoneIds;
  const cropZoneFormValue = mappedCropZoneField
    ? values?.[mappedCropZoneField]
    : values?.cropZones;

  // Initialize cropZonesChecked from the mapped field, extracting IDs if needed
  const initializeCropZonesChecked = () => {
    const mappedField = formikValues?.cropZoneIds;
    const fieldValue = mappedField ? values?.[mappedField] : values?.cropZones;

    if (!fieldValue || !Array.isArray(fieldValue)) {
      return [];
    }

    // If the field contains objects with 'id' property, extract the IDs
    if (
      fieldValue.length > 0 &&
      typeof fieldValue[0] === 'object' &&
      fieldValue[0]?.id
    ) {
      return fieldValue.map(item => item.id);
    }

    // Otherwise, assume it's already an array of IDs or primitives
    return fieldValue;
  };

  const initialCropZones = initializeCropZonesChecked();
  const [cropZonesChecked, setCropZonesChecked] = useState(initialCropZones);
  const savedCropZonesRef = useRef(initialCropZones);
  const amplitude = useContext(AmplitudeContext);
  const searchWrapperRef = useRef(null);
  const { user } = useAuth();

  const [showSelectAllOverlay, setShowSelectAllOverlay] = useState(false);
  const selectAllRafRef = useRef(null);
  const selectAllTimeoutRef = useRef(null);

  const handleSelectAll = useCallback(allIds => {
    // Show overlay immediately — CSS keeps it invisible for 2s via animation-delay.
    // If the work finishes before 2s, the element is removed while still invisible.
    setShowSelectAllOverlay(true);

    // Yield to browser so it paints the invisible overlay element.
    // rAF fires before paint; the setTimeout(0) inside pushes the heavy
    // work to a task that runs after the paint, ensuring the overlay is
    // on-screen before the main thread blocks.
    selectAllRafRef.current = requestAnimationFrame(() => {
      selectAllTimeoutRef.current = setTimeout(() => {
        setCropZonesChecked(allIds);
        setShowSelectAllOverlay(false);
      }, 0);
    });
  }, []);

  const [manualCoords, setManualCoords] = useState(
    getStringParsed(localStorage.getItem(LAST_COORDINATES_STORED))
  );

  const { crops } = useCropData(values?.cropId);

  const {
    loading,
    dataSorted: propertyLandingPageData,
    fieldsAndAreasGeoJSONCollection,
    setGeoJSONCollectionFeatures,
    zoom,
    setZoom,
    fieldsLoading,
    filterFeatureCollectionByParentId,
    reloadData
  } = usePopulateMapData({
    fetchType: FETCH_TYPES.cropSeasonsMap,
    cropSeasonsId: selectedSeasonIds,
    isCacheOn: true
  });

  const geoJSONCentroid = useMemo(() => {
    return JSON.stringify(
      calculateCentroidByEachProperties(
        fieldsAndAreasGeoJSONCollection,
        properties
      )
    );
  }, [fieldsAndAreasGeoJSONCollection, properties]);

  const mapCoordinates = useMemo(
    () => manualCoords || JSON.parse(geoJSONCentroid)?.geometry?.coordinates,
    [geoJSONCentroid, manualCoords]
  );

  useEffect(() => {
    if (open && selectedSeasonIds?.length) {
      reloadData({ seasonIds: selectedSeasonIds });
    }
  }, [open, selectedSeasonIds, reloadData]);

  useEffect(() => {
    if (open) {
      // Snapshot the currently-saved selection when the drawer opens
      savedCropZonesRef.current = initializeCropZonesChecked();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading && !cropZonesChecked?.length) {
      const firstValidProperty = properties?.find(prop =>
        prop?.fields?.some(field => field?.cropzones?.length > 0)
      );
      if (firstValidProperty) {
        setGeoJSONCollectionFeatures(
          filterFeatureCollectionByParentId(
            fieldsAndAreasGeoJSONCollection,
            firstValidProperty.id
          )
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cropZonesChecked, properties]);

  useEffect(() => {
    if (open) {
      const initiallySelectedIds = initializeCropZonesChecked();
      initiallySelectedCropZoneIdsRef.current = new Set(initiallySelectedIds);
      setInitiallySelectedCropZoneIdsSnapshot(initiallySelectedIds);
    } else {
      initiallySelectedCropZoneIdsRef.current = new Set();
      setInitiallySelectedCropZoneIdsSnapshot([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const filteredProperties = filterPropertiesCropZones(
      propertyLandingPageData.properties,
      selectedSeasonIds,
      values?.cropId,
      initiallySelectedCropZoneIdsSnapshot
    );

    setProperties(filteredProperties);
  }, [
    initiallySelectedCropZoneIdsSnapshot,
    open,
    propertyLandingPageData,
    selectedSeasonIds,
    values?.cropId
  ]);

  useEffect(() => {
    if (loading || !cropZonesChecked?.length) {
      return () => {};
    }

    const selectedTargets = cropZonesChecked.map(id => ({ id }));
    const schedule = globalThis.window?.requestAnimationFrame
      ? globalThis.window.requestAnimationFrame.bind(globalThis.window)
      : callback => setTimeout(callback, 0);
    const cancelSchedule = globalThis.window?.cancelAnimationFrame
      ? globalThis.window.cancelAnimationFrame.bind(globalThis.window)
      : timeoutId => clearTimeout(timeoutId);

    const scheduledId = schedule(() => {
      setGeoJSONCollectionFeatures(
        filterFeatureCollectionByParentId(
          fieldsAndAreasGeoJSONCollection,
          undefined,
          selectedTargets
        )
      );
    });

    return () => {
      cancelSchedule(scheduledId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, cropZonesChecked]);

  useEffect(() => {
    if (open) {
      focusInput(searchWrapperRef, 'input');
    }
  }, [open]);

  // Cleanup overlay and pending rAF/timeout when drawer closes or unmounts
  useEffect(() => {
    if (!open) {
      cancelAnimationFrame(selectAllRafRef.current);
      clearTimeout(selectAllTimeoutRef.current);
      setShowSelectAllOverlay(false);
    }
    return () => {
      cancelAnimationFrame(selectAllRafRef.current);
      clearTimeout(selectAllTimeoutRef.current);
    };
  }, [open]);

  // Sync cropZonesChecked with form values when they change externally (e.g., row deletion)
  useEffect(() => {
    const fieldValue = cropZoneFormValue;

    if (!fieldValue || !Array.isArray(fieldValue)) {
      setCropZonesChecked(prevChecked =>
        prevChecked.length > 0 ? [] : prevChecked
      );
      return;
    }

    // Extract IDs if the field contains objects, otherwise use as-is
    const newIds =
      fieldValue.length > 0 &&
      typeof fieldValue[0] === 'object' &&
      fieldValue[0]?.id
        ? fieldValue.map(item => item.id)
        : fieldValue;

    // Only update if the IDs have actually changed to avoid infinite loops
    setCropZonesChecked(prevChecked => {
      if (!isEqual(prevChecked, newIds)) {
        return newIds;
      }
      return prevChecked;
    });
  }, [cropZoneFormValue]);

  // Reset preference load state when the authenticated user changes
  useEffect(() => {
    preferenceLoaded.current = false;
  }, [user?.id]);

  // Load user's group-by preference when the drawer opens and a user is available
  useEffect(() => {
    if (open && user?.id && !preferenceLoaded.current) {
      findPreferenceByKey(PreferenceKey.PROPERTY_GROUP_BY, {
        userId: user.id
      })
        .then(pref => {
          if (pref?.preferenceValue?.groupBy) {
            setGroupBy(pref.preferenceValue.groupBy.toLowerCase());
          }
        })
        .catch(() => {})
        .finally(() => {
          preferenceLoaded.current = true;
        });
    }
  }, [open, findPreferenceByKey, user?.id]);

  const handleGroupByChange = useCallback(
    newGroupBy => {
      setGroupBy(newGroupBy);

      if (!user?.id) {
        return;
      }

      upsertPreference({
        preferenceKey: PreferenceKey.PROPERTY_GROUP_BY,
        preferenceValue: {
          groupBy: newGroupBy.toUpperCase()
        },
        userId: user.id
      }).catch(() => {});
    },
    [upsertPreference, user?.id]
  );

  const renderSortCheckmark = isActive => (
    <svg
      width="20"
      height="19"
      viewBox="0 0 25 24"
      fill="none"
      className="flex-shrink-0 mr-1 ml-2"
    >
      <path
        d="M8.8273 15.8769L4.6573 11.7069L3.2373 13.1169L8.8273 18.7069L20.8273 6.70687L19.4173 5.29688L8.8273 15.8769Z"
        fill={isActive ? '#0092E4' : 'transparent'}
      />
    </svg>
  );

  const sortMenuItems = [
    {
      key: GROUP_BY_CROP,
      className: groupBy === GROUP_BY_CROP ? 'selected-item' : '',
      label: (
        <div
          className={`flex items-center w-full h-full ${
            groupBy === GROUP_BY_CROP ? 'font-semibold' : ''
          }`}
        >
          {renderSortCheckmark(groupBy === GROUP_BY_CROP)}
          <span className="px-2 py-1">Group by crop</span>
        </div>
      )
    },
    {
      key: GROUP_BY_FARM,
      className: groupBy === GROUP_BY_FARM ? 'selected-item' : '',
      label: (
        <div
          className={`flex items-center w-full h-full ${
            groupBy === GROUP_BY_FARM ? 'font-semibold' : ''
          }`}
        >
          {renderSortCheckmark(groupBy === GROUP_BY_FARM)}
          <span className="px-2 py-1">Group by farm</span>
        </div>
      )
    }
  ];

  const sortMenuProps = {
    items: sortMenuItems,
    onClick: ({ key }) => handleGroupByChange(key)
  };

  const totalArea = useMemo(() => {
    return getTotalAreaNumber(
      matchFeaturesAndSelectedCropzones(
        fieldsAndAreasGeoJSONCollection,
        cropZonesChecked
      )
    );
  }, [cropZonesChecked, fieldsAndAreasGeoJSONCollection]);

  const filteredProperties = useMemo(() => {
    return searchByTextWithinFFT(properties, searchTextValueFFT.valueInput);
  }, [properties, searchTextValueFFT]);

  // Transform properties for crop grouping mode
  const groupedProperties = useMemo(() => {
    if (groupBy === GROUP_BY_CROP && filteredProperties?.length > 0) {
      return groupByCrop(filteredProperties);
    }
    return filteredProperties;
  }, [groupBy, filteredProperties]);

  const toggleCropZonesSelection = (prevSelection, targets) => {
    const ids = targets.map(({ id }) => id);
    const anyNotSelected = ids.some(id => !prevSelection.includes(id));
    if (anyNotSelected) {
      return [...new Set([...prevSelection, ...ids])];
    }
    return prevSelection.filter(id => !ids.includes(id));
  };

  const onSelectFromMap = useCallback(event => {
    (event?.features || []).forEach(clicked => {
      if (clicked?.properties?.$landType === 'field') {
        const targets = JSON.parse(clicked?.properties?.cropzones);
        if (targets?.length > 0) {
          setCropZonesChecked(prev => toggleCropZonesSelection(prev, targets));
        }
      }
    });
  }, []);

  const cropSeasonHandler = () =>
    cropSeasons
      .filter(cropSeason => getSeasons().includes(cropSeason.id))
      .map(season => season.name)
      .join(', ');

  const handleDiscardPropertySelection = () => {
    setCropZonesChecked(savedCropZonesRef.current);
    onClose();
  };

  const hasUnsavedChanges = () => {
    const saved = savedCropZonesRef.current;
    if (saved.length !== cropZonesChecked.length) return true;
    const savedSet = new Set(saved);
    return cropZonesChecked.some(id => !savedSet.has(id));
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      const {
        title,
        content,
        okText,
        cancelText
      } = LayoutStrings.modals.discardPropertySelection;
      showConfirmModal({
        title,
        content: <span className="text-sm">{content}</span>,
        okText,
        cancelText,
        icon: <ReportProblemIcon />,
        okButtonProps: {
          style: {
            borderRadius: 4,
            background: '#14803C',
            padding: '10px 16px',
            color: '#FFF',
            fontSize: 14,
            fontWeight: 400,
            marginLeft: 8
          }
        },
        onOk: handleDiscardPropertySelection,
        width: 423
      });
    } else {
      onClose();
    }
  };

  // Helper function to get field geometry for a crop zone
  const getCropzoneFeatures = fieldId =>
    fieldsAndAreasGeoJSONCollection.features?.filter(
      feature => fieldId === feature.properties.id
    ) || [];

  // Helper function to check if field contains cropzone
  const fieldContainsCropzone = (field, cropzoneId) => {
    return field.cropzones.some(cz => cz.id === cropzoneId);
  };

  // Helper function to check if property contains cropzone
  const propertyContainsCropzone = (property, cropzoneId) => {
    return property.fields.some(field =>
      fieldContainsCropzone(field, cropzoneId)
    );
  };

  // Helper function to find field info for a crop zone
  const findFieldInfoForCropzone = cropzoneId => {
    const property = propertyLandingPageData.properties.find(prop =>
      propertyContainsCropzone(prop, cropzoneId)
    );

    if (!property) {
      return { fieldId: null, fieldName: '' };
    }

    const field = property.fields.find(_field =>
      fieldContainsCropzone(_field, cropzoneId)
    );

    return field
      ? { fieldId: field.id, fieldName: field.name }
      : { fieldId: null, fieldName: '' };
  };

  // Helper function to enhance a single crop zone with field data
  const enhanceCropzoneWithFieldData = cropzone => {
    const { fieldId, fieldName } = findFieldInfoForCropzone(cropzone.id);

    return {
      ...cropzone,
      area: cropzone.reportedArea || cropzone.boundaryArea,
      fieldName,
      fieldId,
      fieldGeometry: fieldId ? getCropzoneFeatures(fieldId) : []
    };
  };

  const onSelectionApply = () => {
    // Get selected crop zones with field geometry included
    const baseCropZones = getSelectedCropZones(
      propertyLandingPageData.properties,
      cropZonesChecked
    );

    // Build a lookup of existing crop zones to preserve user-edited values
    const existingCropZones = values?.[formikValues?.cropZones] || [];
    const existingById = {};
    if (Array.isArray(existingCropZones)) {
      existingCropZones.forEach(zone => {
        if (zone?.id) {
          existingById[zone.id] = zone;
        }
      });
    }

    // Enhance crop zones with field geometry and area,
    // preserving edited values (coverage, appliedAcreage, etc.) for existing zones
    const mappedCropZones = baseCropZones.map(cropzone => {
      const enhanced = enhanceCropzoneWithFieldData(cropzone);
      const existing = existingById[cropzone.id];
      if (existing) {
        return {
          ...enhanced,
          area: existing.area ?? enhanced.area,
          appliedAcreage: existing.appliedAcreage,
          coveragePercent: existing.coveragePercent,
          selectedAreaType: existing.selectedAreaType
        };
      }
      return enhanced;
    });

    setFieldValue(formikValues.cropZoneIds, cropZonesChecked);
    setFieldValue(formikValues.totalArea, totalArea);
    setFieldValue(formikValues.cropZones, mappedCropZones);
    setFieldValue(formikValues.sourceType, 'CROP_ZONE');

    amplitude.sendEventToAmplitude(
      amplitude.events.epic.YieldV2.addYield.saveProperties,
      { numberOfCrops: mappedCropZones.length }
    );
    // Mark current selection as saved before closing
    savedCropZonesRef.current = [...cropZonesChecked];
    onClose();
  };

  const isReloadingProperties =
    loading || fieldsLoading || isGlobalCropSeasonLoading;

  return (
    <Drawer
      width="80%"
      open={open}
      onClose={handleClose}
      bodyStyle={{ overflow: 'hidden' }}
      className="select-properties-drawer"
    >
      <div data-testid="select-properties-drawer">
        <header className="flex flex-row mb-4">
          <div className="w-1/3 mr-6">
            <h1 className="font-semibold text-xl mb-1">Select Properties</h1>
            <p className="text-left text-sm font-normal leading-5 tracking-tighter text-neutral-60">
              (choose from list or map)
            </p>
          </div>

          <div className="w-2/3">
            <div className="font-normal text-base">
              Crop Season: {cropSeasonHandler()}
            </div>
            <div className="flex flex-row mt-1">
              <div className="mr-1">
                {`${cropZonesChecked.length} crop zone${
                  cropZonesChecked.length === 1 ? '' : 's'
                } selected`}
              </div>
              <img className="mx-2" src={Divider} alt="divider icon" />
              <img className="mx-1" src={FieldIcon} alt="field icon" />
              <div>Total area: {totalArea.toFixed(2)} ac</div>
            </div>
          </div>
        </header>

        <div className="flex flex-row">
          <section className="flex flex-col w-1/3 mr-6 tree-left-panel">
            {type === 'Load' && (
              <div className="flex flex-col gap-y-2 mb-4">
                <div className="text-base flex items-center gap-x-1">
                  Crop: <CropIconMapping cropObject={crops} />
                  {crops?.name}
                </div>
                <div className="flex text-xs">
                  <img className="mr-1" src={InfoIcon} alt="info icon" />
                  <span>
                    Only properties matching the selected crop are listed
                  </span>
                </div>
              </div>
            )}
            <div className="flex items-start gap-2">
              <div className="flex-1" ref={searchWrapperRef}>
                <SearchInputLandingProperties
                  drawer
                  loadDetailsDrawer
                  onChange={event => {
                    const valueInput = event.target.value.toLowerCase();
                    dispatch({
                      type: SEARCH_TEXT_VALUE_FFT,
                      payload: { valueInput }
                    });
                  }}
                  value={searchTextValueFFT?.valueInput}
                  placeholder="Search Properties"
                />
              </div>
              {/* Using Ant Design Dropdown because Syngenta UI Kit Dropdown doesn't support custom trigger */}
              <Dropdown
                menu={sortMenuProps}
                trigger={['click']}
                placement="bottomRight"
                overlayClassName="group-by-dropdown"
              >
                <Tooltip title="Group by" placement="top" color="#232630">
                  <button
                    type="button"
                    aria-label="Group by"
                    data-testid="group-by-sort-button"
                    className="w-8 h-8 flex items-center justify-center rounded bg-white border border-neutral-30 hover:bg-neutral-100 flex-shrink-0"
                  >
                    <img src={SortIcon} alt="" aria-hidden="true" />
                  </button>
                </Tooltip>
              </Dropdown>
            </div>
            {isReloadingProperties ? (
              <div className="w-full pt-54px flex flex-col items-center justify-start">
                <Spinner />
                <p className="text-base mt-4">Loading farms...</p>
              </div>
            ) : (
              <>
                <div className="font-semibold text-base mt-4">PROPERTIES</div>
                <div className="flex-1 overflow-scroll">
                  <PropertySelector
                    properties={groupedProperties}
                    cropZonesChecked={cropZonesChecked}
                    setCropZonesChecked={setCropZonesChecked}
                    setGeoJSONCollectionFeatures={setGeoJSONCollectionFeatures}
                    fieldsAndAreasGeoJSONCollection={
                      fieldsAndAreasGeoJSONCollection
                    }
                    fieldsLoading={fieldsLoading}
                    filterFeatureCollectionByParentId={
                      filterFeatureCollectionByParentId
                    }
                    groupBy={groupBy}
                    onSelectAll={handleSelectAll}
                  />
                </div>
              </>
            )}
          </section>

          <section className="w-2/3">
            <PropertyMapWrapper
              fieldsLoading={fieldsLoading}
              geoJSONCollection={fieldsAndAreasGeoJSONCollection}
              zoom={zoom}
              setZoom={setZoom}
              currentCoordinates={mapCoordinates}
              setCurrentCoordinates={setManualCoords}
              onClickShape={isReloadingProperties ? undefined : onSelectFromMap}
              customStyles={cropZonesChecked.length > 0}
              stretch
              liveUpdate
              displayOnly
            />
          </section>

          <footer className="absolute bottom-0 w-full right-0 left-0 flex justify-end items-center border-t border-neutral-20 p-2 gap-3">
            <Button ghost type="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={onSelectionApply}
              disabled={!cropZonesChecked?.length || isReloadingProperties}
            >
              Save Selections
            </Button>
          </footer>
        </div>
        {showSelectAllOverlay && (
          <>
            <div className="select-all-loading-backdrop" />
            <div
              className="select-all-loading-container"
              data-testid="select-all-loading-overlay"
              role="status"
              aria-live="polite"
            >
              <div className="select-all-loading-panel">
                <img
                  src={SpinnerIcon}
                  alt="spinner"
                  width="72"
                  height="72"
                  className="animate-spin"
                  style={{ animationDuration: '2s' }}
                  aria-hidden="true"
                />
                <span className="select-all-loading-text">
                  Selecting properties...
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

SelectPropertiesDrawer.defaultProps = {
  type: '',
  formikValues: {
    cropZoneIds: 'cropZones',
    totalArea: 'totalArea',
    cropZones: 'sources',
    sourceType: 'sourceType'
  },
  seasonIds: []
};

SelectPropertiesDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  seasonIds: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string,
  formikValues: PropTypes.shape({
    cropZoneIds: PropTypes.string,
    totalArea: PropTypes.string,
    cropZones: PropTypes.string,
    sourceType: PropTypes.string
  })
};

export default SelectPropertiesDrawer;
